// src/components/Dashboard/ClaimExtendedInfo.jsx
// Muestra (solo lectura) los datos del claimRecord que no están en el
// encabezado de la ficha: caso y personas (22-sep), propiedad y
// dueños adicionales, aseguradora/agente/ajustador de la aseguradora,
// coberturas/deducible y mortgage. Se edita desde el panel admin
// (CreateClaimRecord.jsx) — acá es de solo consulta.
// No se renderiza nada si el claimRecord no tiene ninguno de estos campos
// llenos (reclamos viejos, o si el admin nunca completó "Detalles
// adicionales" al crearlo).
import React, { useContext, useState } from 'react';
import { LanguageContext } from '../../App';

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const Row = ({ label, value }) => {
  if (!value) return null;
  return <div className="cd-claim-row"><span className="cd-label">{label}</span><span className="cd-value">{value}</span></div>;
};

// defaultOpen: en la pestaña "Datos" de la ficha se muestra desplegado.
const ClaimExtendedInfo = ({ claimRecord, defaultOpen = false }) => {
  const { language } = useContext(LanguageContext);
  const [open, setOpen] = useState(defaultOpen);
  const es = language === 'es';

  if (!claimRecord) return null;

  const owners = [claimRecord.owner2, claimRecord.owner3].filter(o => o && (o.firstName || o.lastName));
  const lossAddress = [claimRecord.lossAddressStreet, claimRecord.lossAddressCity, claimRecord.lossAddressState, claimRecord.lossAddressZip].filter(Boolean).join(', ');
  const mailingAddress = claimRecord.mailingSameAsLoss
    ? null
    : [claimRecord.mailingAddressStreet, claimRecord.mailingAddressCity, claimRecord.mailingAddressState, claimRecord.mailingAddressZip].filter(Boolean).join(', ');
  const hasCoverages = [claimRecord.coverageA, claimRecord.coverageB, claimRecord.coverageC, claimRecord.coverageD].some(v => v !== null && v !== undefined && v !== '');
  const deductible = claimRecord.deductibleAmount !== null && claimRecord.deductibleAmount !== undefined && claimRecord.deductibleAmount !== ''
    ? (claimRecord.deductibleType === 'percentage' ? `${claimRecord.deductibleAmount}%` : formatMoney(claimRecord.deductibleAmount))
    : null;

  const COVERAGE = { approved: ['Aprobada', 'Approved'], partial_denial: ['Parcialmente negada', 'Partial denial'], below_deductible: ['Bajo el deducible', 'Below deductible'], denied: ['Negada', 'Denied'] };
  const contacts = (claimRecord.additionalContacts || []).filter(c => c.name || c.phone || c.email);
  const hasCase = claimRecord.agreedFeePercent != null || claimRecord.claimType || claimRecord.lossDescription || claimRecord.lossTime
    || claimRecord.coverageDecision || claimRecord.signatureObtainedBy || claimRecord.negotiator
    || claimRecord.mitigationCompany || claimRecord.attorneyName || contacts.length > 0 || claimRecord.commercialEntityName
    || claimRecord.contractSignedDate || claimRecord.dateCalledIn || claimRecord.vendor;

  const hasAnything = hasCase || claimRecord.propertyType || owners.length > 0 || lossAddress || mailingAddress
    || claimRecord.deskAdjusterName
    || claimRecord.insuranceClaimEmail || claimRecord.insurancePhone || claimRecord.agentName
    || claimRecord.insuranceAdjusterName || hasCoverages || deductible
    || claimRecord.mortgageCompanyName;

  if (!hasAnything) return null;

  return (
    <div className="cd-attach-section">
      <button type="button" className="cd-detail-checks-header cd-extended-toggle" onClick={() => setOpen(v => !v)}>
        <h2 className="cd-detail-checks-title">{es ? 'Detalles adicionales del reclamo' : 'Additional claim details'}</h2>
        <span className="cd-detail-checks-count"><i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`}></i></span>
      </button>

      {open && (
        <div className="cd-detail-panels">
          {hasCase && (
            <div className="cd-detail-panel">
              <h2>{es ? 'Caso y personas' : 'Case & people'}</h2>
              <Row label={es ? '% de honorario acordado' : 'Agreed fee %'} value={claimRecord.agreedFeePercent != null && claimRecord.agreedFeePercent !== '' ? `${claimRecord.agreedFeePercent}%` : ''} />
              <Row label={es ? 'Tipo de reclamo' : 'Claim type'} value={{ emergency: 'Emergency', non_emergency: 'Non Emergency', supplemental: 'Supplemental' }[claimRecord.claimType]} />
              <Row label={es ? 'Hora de la pérdida' : 'Time of loss'} value={claimRecord.lossTime} />
              <Row label={es ? 'Descripción de la pérdida' : 'Loss description'} value={claimRecord.lossDescription} />
              <Row label={es ? 'Decisión de cobertura' : 'Coverage decision'} value={COVERAGE[claimRecord.coverageDecision] ? (es ? COVERAGE[claimRecord.coverageDecision][0] : COVERAGE[claimRecord.coverageDecision][1]) : ''} />
              <Row label={es ? 'Compañía (comercial)' : 'Company (commercial)'} value={[claimRecord.commercialEntityName, claimRecord.commercialEntityPhone, claimRecord.commercialEntityEmail].filter(Boolean).join(' · ')} />
              <Row label={es ? 'Quién obtuvo la firma' : 'Signature obtained by'} value={claimRecord.signatureObtainedBy} />
              <Row label={es ? 'Negociador' : 'Negotiator'} value={claimRecord.negotiator} />
              <Row label={es ? 'Compañía de mitigación' : 'Mitigation company'} value={claimRecord.mitigationCompany} />
              <Row label={es ? 'Abogado' : 'Attorney'} value={[claimRecord.attorneyName, claimRecord.attorneyEmail].filter(Boolean).join(' · ')} />
              <Row label={es ? 'Contrato firmado' : 'Contract signed'} value={claimRecord.contractSignedDate} />
              <Row label={es ? 'Fecha en que llamó' : 'Date called in'} value={claimRecord.dateCalledIn} />
              <Row label="Vendor" value={claimRecord.vendor} />
              {contacts.map((c, i) => (
                <Row key={i} label={`${es ? 'Contacto' : 'Contact'} ${i + 2}`} value={[c.name, c.relation && `(${c.relation})`, c.phone, c.email].filter(Boolean).join(' · ')} />
              ))}
            </div>
          )}
          {(owners.length > 0 || claimRecord.propertyType || lossAddress || mailingAddress) && (
            <div className="cd-detail-panel">
              <h2>{es ? 'Propiedad y dueños' : 'Property & owners'}</h2>
              <Row label={es ? 'Tipo de propiedad' : 'Property type'} value={claimRecord.propertyType === 'residential' ? (es ? 'Residencial' : 'Residential') : claimRecord.propertyType === 'commercial' ? (es ? 'Comercial' : 'Commercial') : ''} />
              <Row label={es ? 'Dirección de la pérdida' : 'Loss address'} value={lossAddress} />
              {mailingAddress && <Row label={es ? 'Dirección de correo' : 'Mailing address'} value={mailingAddress} />}
              {owners.map((owner, i) => (
                <Row key={i} label={es ? `Dueño ${i + 2}` : `Owner ${i + 2}`} value={[`${owner.firstName} ${owner.lastName}`.trim(), owner.phone, owner.email].filter(Boolean).join(' · ')} />
              ))}
            </div>
          )}

          {(claimRecord.insuranceClaimEmail || claimRecord.insurancePhone || claimRecord.agentName || claimRecord.insuranceAdjusterName || claimRecord.deskAdjusterName) && (
            <div className="cd-detail-panel">
              <h2>{es ? 'Aseguradora, agente y ajustador' : 'Insurance, agent & adjuster'}</h2>
              <Row label={es ? 'Correo de reclamos' : 'Claims email'} value={claimRecord.insuranceClaimEmail} />
              <Row label={es ? 'Teléfono general' : 'General phone'} value={claimRecord.insurancePhone} />
              <Row label={es ? 'Agente de seguros' : 'Insurance agent'} value={[claimRecord.agentName, claimRecord.agentPhone].filter(Boolean).join(' · ')} />
              <Row label={es ? 'Dirección de la agencia' : 'Agency address'} value={claimRecord.agentAddress} />
              <Row label="Field Adjuster (FA)" value={[claimRecord.insuranceAdjusterName, claimRecord.insuranceAdjusterPhone, claimRecord.insuranceAdjusterEmail].filter(Boolean).join(' · ')} />
              <Row label="Desk Adjuster (DA)" value={[claimRecord.deskAdjusterName, claimRecord.deskAdjusterPhone, claimRecord.deskAdjusterEmail].filter(Boolean).join(' · ')} />
            </div>
          )}

          {(hasCoverages || deductible) && (
            <div className="cd-detail-panel">
              <h2>{es ? 'Coberturas' : 'Coverages'}</h2>
              <Row label="Coverage A" value={formatMoney(claimRecord.coverageA)} />
              <Row label="Coverage B" value={formatMoney(claimRecord.coverageB)} />
              <Row label="Coverage C" value={formatMoney(claimRecord.coverageC)} />
              <Row label="Coverage D" value={formatMoney(claimRecord.coverageD)} />
              <Row label={es ? 'Deducible' : 'Deductible'} value={deductible} />
            </div>
          )}

          {claimRecord.mortgageCompanyName && (
            <div className="cd-detail-panel">
              <h2>{es ? 'Compañía hipotecaria' : 'Mortgage company'}</h2>
              <Row label={es ? 'Nombre' : 'Name'} value={claimRecord.mortgageCompanyName} />
              <Row label={es ? 'Número de préstamo' : 'Loan number'} value={claimRecord.mortgageLoanNumber} />
              <Row label={es ? 'Teléfono' : 'Phone'} value={claimRecord.mortgagePhone} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClaimExtendedInfo;
